import { useEffect, useState } from "react";
import { Loader2, Phone } from "lucide-react";
import { Field } from "./Field";
import { SuccessMessage } from "./SuccessMessage";
import { EMPTY_FORM, validate } from "./validate";
import { PHONE_DISPLAY, PHONE_HREF, SERVICE_OPTIONS, TOWN_OPTIONS } from "./content";

const FORM_ENDPOINT = "https://forms.elido.nu/api/contact";
const SITE_ID = "dellsjo";

const freshSubmissionId = () =>
  typeof window === "undefined" ? "" : crypto.randomUUID();

export const QuoteForm = () => {
  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submissionId, setSubmissionId] = useState(freshSubmissionId);

  useEffect(() => {
    const onServiceSelect = (event) => {
      const service = event.detail?.service;
      if (!SERVICE_OPTIONS.includes(service)) return;

      setSent(false);
      setSubmitError("");
      setValues((current) => ({ ...current, service }));
      setSubmissionId(crypto.randomUUID());
      setErrors((current) => ({ ...current, service: undefined }));
    };

    window.addEventListener("dellsjo:select-service", onServiceSelect);
    return () => window.removeEventListener("dellsjo:select-service", onServiceSelect);
  }, []);

  const set = (key) => (event) => {
    const next = { ...values, [key]: event.target.value };
    setValues(next);
    setSubmissionId(crypto.randomUUID());
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
    if (submitError) setSubmitError("");
  };

  const aria = (key) => ({
    id: key,
    name: key,
    "aria-invalid": Boolean(errors[key]),
    "aria-describedby": errors[key] ? `${key}-error` : undefined,
  });

  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;

    const found = validate(values);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(first)?.focus();
      return;
    }

    setSending(true);
    setSubmitError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_id: SITE_ID,
          submission_id: submissionId,
          website: values.website,
          subject: `Offertförfrågan – ${values.service}`,
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim() || null,
          message: values.description.trim(),
          service: values.service,
          city: values.town,
          description: values.description.trim(),
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok || !payload?.emailed) {
        throw new Error("request_failed");
      }

      setSent(true);
      setValues(EMPTY_FORM);
      setSubmissionId(crypto.randomUUID());

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "contact_form_success",
        method: "web_form",
        site_id: SITE_ID,
      });
    } catch {
      setSubmitError(
        "Något gick fel när förfrågan skulle skickas. Försök igen eller ring Mats på 070-715 38 62."
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <section id="offert" className="bg-white py-14 md:py-20">
        <div className="mx-auto grid max-w-6xl gap-9 px-4 sm:px-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Offert</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Tack för din förfrågan</h2>
          </div>
          <div className="lg:col-span-8">
            <SuccessMessage onReset={() => setSent(false)} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="offert" className="bg-white py-14 md:py-20">
      <div className="mx-auto grid max-w-6xl gap-9 px-4 sm:px-6 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <p className="eyebrow">Offert</p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">En enkel väg till offert</h2>
          <p className="mt-4 text-[var(--ink-soft)] md:text-lg">
            Välj tjänst och ort, beskriv kort vad du behöver hjälp med och lämna dina kontaktuppgifter.
          </p>
          <div className="mt-6 rounded-2xl bg-[var(--paper)] p-5">
            <p className="font-display text-lg font-bold">Hellre ringa?</p>
            <div className="mt-2">
              <a href={PHONE_HREF} className="inline-flex items-center gap-2 text-lg font-semibold text-[var(--red)] underline-offset-4 hover:underline">
                <Phone size={18} aria-hidden /> {PHONE_DISPLAY}
              </a>
              <p className="ml-7 mt-0.5 text-sm font-semibold text-[var(--red)]">Mats</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          <form
            id="kontaktformular"
            noValidate
            onSubmit={submit}
            className="grid gap-5 rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-5 sm:grid-cols-2 sm:p-8"
            aria-labelledby="form-title"
          >
            <div className="hidden" aria-hidden="true">
              <label htmlFor="website">Lämna detta fält tomt</label>
              <input
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={values.website}
                onChange={set("website")}
              />
            </div>

            <div className="sm:col-span-2">
              <h3 id="form-title" className="font-display text-2xl font-bold">
                Berätta kort vad du behöver hjälp med
              </h3>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">
                Mats återkommer via kontaktuppgifterna du anger.
              </p>
            </div>

            <Field id="service" label="Tjänst" error={errors.service}>
              <select required className="field" value={values.service} onChange={set("service")} {...aria("service")}>
                <option value="">Välj tjänst</option>
                {SERVICE_OPTIONS.map((option) => <option key={option}>{option}</option>)}
              </select>
            </Field>

            <Field id="town" label="Ort" error={errors.town}>
              <select required className="field" value={values.town} onChange={set("town")} {...aria("town")}>
                <option value="">Välj ort</option>
                {TOWN_OPTIONS.map((option) => <option key={option}>{option}</option>)}
              </select>
            </Field>

            <div className="sm:col-span-2">
              <Field id="description" label="Beskrivning av uppdraget" error={errors.description}>
                <textarea
                  required
                  rows={5}
                  className="field"
                  placeholder="T.ex. kontor på ca 200 kvm, städning varannan vecka"
                  value={values.description}
                  onChange={set("description")}
                  {...aria("description")}
                />
              </Field>
            </div>

            <Field id="name" label="Namn" error={errors.name}>
              <input required className="field" autoComplete="name" value={values.name} onChange={set("name")} {...aria("name")} />
            </Field>

            <Field id="email" label="E-post" error={errors.email}>
              <input required type="email" inputMode="email" autoComplete="email" className="field" value={values.email} onChange={set("email")} {...aria("email")} />
            </Field>

            <Field id="phone" label="Telefon" optional error={errors.phone}>
              <input type="tel" inputMode="tel" autoComplete="tel" className="field" value={values.phone} onChange={set("phone")} {...aria("phone")} />
            </Field>

            <div className="flex flex-col justify-end gap-3 sm:col-span-2">
              <p className="text-xs leading-relaxed text-[var(--ink-soft)]">
                Genom att skicka förfrågan godkänner du att uppgifterna används för att besvara ditt meddelande.
              </p>

              {submitError && (
                <p role="alert" aria-live="assertive" className="rounded-xl border border-[var(--red)] bg-white px-4 py-3 text-sm font-semibold text-[var(--red)]">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="btn btn-red w-full disabled:cursor-wait disabled:opacity-60 sm:w-auto sm:self-start"
              >
                {sending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" aria-hidden />
                    Skickar…
                  </>
                ) : (
                  "Skicka offertförfrågan"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
